/**
 * Presentation helpers for the User Analytics page.
 *
 * The recurring theme: the API distinguishes "no value" from zero, and so must
 * the display. An unpriced model is not a free one, a window with no completed
 * session has no average duration, and a metric with no preceding window has
 * no trend. Each of those renders as an explicit placeholder rather than a
 * number the data does not support.
 */

/** Shown where a figure genuinely does not exist. Never used for a real zero. */
export const NOT_AVAILABLE = "\u2014";

/** Shown for usage whose model has no known rate, which is not the same as $0. */
export const UNPRICED_LABEL = "Not priced";

const countFormatter = new Intl.NumberFormat("en-US");

/** Thousands-separated integer, e.g. `1,234`. */
export const formatCount = (value: number): string =>
  countFormatter.format(Math.round(value));

/**
 * Point at which the ``K`` form would round up to a whole thousand.
 *
 * Not ``1_000_000``: the boundary has to be where the *rendered* value rolls
 * over, not where the true value does. One decimal place means anything from
 * 999,950 up displays as ``1000.0K``, which is a unit nobody writes. These
 * counts belong in the ``M`` form, where 999,950 reads cleanly as ``1.00M``.
 */
const K_FORM_LIMIT = 999_950;

/**
 * Compact token count: exact below a thousand, abbreviated above so that wide
 * counts stay readable in a table cell.
 */
export const formatTokens = (value: number): string => {
  const rounded = Math.round(value);
  if (rounded < 1_000) {
    return countFormatter.format(rounded);
  }
  if (rounded < K_FORM_LIMIT) {
    return `${(rounded / 1_000).toFixed(1)}K`;
  }
  return `${(rounded / 1_000_000).toFixed(2)}M`;
};

/** Below this, a two-decimal amount would round to $0.00 and read as free. */
const SUB_CENT_THRESHOLD = 0.005;

/** Below this, even four decimals render as zeroes, so say "less than" instead. */
const SMALLEST_SHOWN_USD = 0.0001;

/**
 * US dollars, or the unpriced placeholder when the cost is unknown.
 *
 * Amounts too small to show in cents keep four decimals rather than rounding to
 * `$0.00`: real spend displayed as zero is the same misreading the unpriced
 * placeholder exists to prevent. Amounts smaller than four decimals can express
 * are reported as a bound rather than as a row of zeroes, for the same reason.
 * Everything else uses cents.
 */
export const formatUsd = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return UNPRICED_LABEL;
  }
  if (value === 0) {
    return "$0.00";
  }
  const magnitude = Math.abs(value);
  if (magnitude < SMALLEST_SHOWN_USD) {
    return `<$${SMALLEST_SHOWN_USD.toFixed(4)}`;
  }
  if (magnitude < SUB_CENT_THRESHOLD) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(2)}`;
};

/** Whole-second duration as `1h 02m 03s` / `2m 03s` / `45s`, or a placeholder. */
export const formatDurationSeconds = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return NOT_AVAILABLE;
  }
  const total = Math.max(0, Math.round(value));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(
      seconds
    ).padStart(2, "0")}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }
  return `${seconds}s`;
};

/** A 0-to-1 fraction as a percentage with one decimal, e.g. `42.5%`. */
export const formatShare = (fraction: number): string =>
  `${(fraction * 100).toFixed(1)}%`;

/** A rate such as questions per user, or a placeholder when there is no denominator. */
export const formatRate = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return NOT_AVAILABLE;
  }
  return value.toFixed(1);
};

export type TrendDirection = "up" | "down" | "flat" | "new";

export interface Trend {
  direction: TrendDirection;
  label: string;
}

/**
 * Compare a figure with the preceding window.
 *
 * Returns null when no comparison is possible, so the caller renders nothing
 * rather than implying a flat trend. A prior window of zero cannot produce a
 * percentage, so that case is described in words instead of as a ratio.
 */
export const computeTrend = (
  current: number,
  prior: number | null
): Trend | null => {
  if (prior === null || prior === undefined || Number.isNaN(prior)) {
    return null;
  }
  if (prior === 0) {
    if (current === 0) {
      return { direction: "flat", label: "No change" };
    }
    return { direction: "new", label: "No prior activity" };
  }

  const change = ((current - prior) / prior) * 100;
  if (Math.abs(change) < 0.05) {
    return { direction: "flat", label: "No change" };
  }
  const rounded = Math.abs(change).toFixed(1);
  return change > 0
    ? { direction: "up", label: `+${rounded}% vs previous period` }
    : { direction: "down", label: `-${rounded}% vs previous period` };
};

/** `2026-07-23` rendered as a short, locale-independent axis label. */
export const formatDayLabel = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return isoDate;
  }
  const [, , month, day] = match;
  return `${month}/${day}`;
};
